import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import axios from 'axios';
import { useSearch } from '../components/Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const bp = {
  xs: '360px', sm: '480px', md: '768px', lg: '1024px', xl: '1280px',
};

// ===================== ANIMATIONS =====================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const popIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const fadeOverlay = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const shimmer = keyframes`
  0%   { background-position: -800px 0; }
  100% { background-position: 800px 0; }
`;
const float = keyframes`
  0%,100% { transform: translateY(0) rotate(0deg); }
  25%      { transform: translateY(-5px) rotate(1deg); }
  75%      { transform: translateY(3px) rotate(-1deg); }
`;
const pulse = keyframes`
  0%,100% { transform: scale(1); }
  50%      { transform: scale(1.04); }
`;
const staggerIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const ripple = keyframes`
  0%   { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.5); opacity: 0; }
`;

// ===================== LAYOUT =====================
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #f0f7ff 0%, #e8f1fb 30%, #f5f8fc 60%, #edf4ff 100%);
  padding: 24px 20px 80px;

  @media (max-width: ${bp.md}) { padding: 16px 12px 100px; }
  @media (max-width: ${bp.sm}) { padding: 12px 10px 100px; }
`;

const Container = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

// ===================== HERO =====================
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 20px;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 35%, #0284c7 65%, #0ea5e9 100%);
  padding: 40px 44px;
  box-shadow: 0 4px 6px rgba(3,105,161,0.08), 0 16px 40px rgba(3,105,161,0.15);

  @media (max-width: ${bp.md}) { padding: 28px 20px; border-radius: 16px; margin-bottom: 16px; }
  @media (max-width: ${bp.sm}) { padding: 22px 16px; border-radius: 14px; }
`;

const HeroBg = styled.div`
  position: absolute; inset: 0; overflow: hidden; pointer-events: none;
  svg { position: absolute; width: 100%; height: 100%; }
`;

const HeroGrid = styled.div`
  position: relative; z-index: 2;
  display: flex; align-items: center; justify-content: space-between; gap: 32px;
  @media (max-width: ${bp.sm}) { flex-direction: column-reverse; gap: 16px; text-align: center; }
`;

const HeroLeft = styled.div`
  flex: 1; min-width: 0;
`;

const HeroBadge = styled.div`
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(255,255,255,0.12); backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.15); border-radius: 100px;
  padding: 5px 14px 5px 7px; margin-bottom: 16px;
  animation: ${fadeIn} 0.45s ease 0.1s backwards;
  @media (max-width: ${bp.sm}) { margin-bottom: 10px; }
`;

const BadgeDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%; background: #67e8f9;
  position: relative; flex-shrink: 0;
  &::after {
    content: ''; position: absolute; inset: -3px; border-radius: 50%;
    background: #67e8f9; animation: ${ripple} 2s ease-out infinite;
  }
`;

const BadgeText = styled.span`
  font-size: 0.73rem; font-weight: 600; color: rgba(255,255,255,0.9); letter-spacing: 0.3px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.4rem, 4vw, 2rem); font-weight: 800; color: white;
  letter-spacing: -0.5px; margin: 0 0 10px; line-height: 1.2;
  animation: ${fadeIn} 0.45s ease 0.15s backwards;
`;

const HeroDesc = styled.p`
  font-size: clamp(0.82rem, 2vw, 0.98rem); color: rgba(255,255,255,0.78);
  margin: 0; line-height: 1.6; max-width: 420px;
  animation: ${fadeIn} 0.45s ease 0.2s backwards;
  @media (max-width: ${bp.sm}) { max-width: 100%; }
`;

const HeroStats = styled.div`
  display: flex; gap: 20px; margin-top: 20px; flex-wrap: wrap;
  animation: ${fadeIn} 0.45s ease 0.25s backwards;
  @media (max-width: ${bp.sm}) { justify-content: center; gap: 14px; }
`;

const HeroStat = styled.div`
  .number { font-size: clamp(1.2rem, 3vw, 1.5rem); font-weight: 800; color: white; line-height: 1; margin-bottom: 3px; }
  .label  { font-size: 0.7rem; color: rgba(255,255,255,0.6); font-weight: 500; }
`;

const HeroStatDiv = styled.div`
  width: 1px; background: rgba(255,255,255,0.15); align-self: stretch;
`;

const HeroVisual = styled.div`
  flex-shrink: 0; width: 160px; height: 130px;
  animation: ${float} 6s ease-in-out infinite, ${fadeIn} 0.6s ease 0.3s backwards;
  @media (max-width: ${bp.md}) { width: 130px; height: 105px; }
  @media (max-width: ${bp.sm}) { width: 100px; height: 80px; }
`;

// ===================== TOOLBAR =====================
const ToolbarRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
  animation: ${fadeIn} 0.4s ease 0.1s backwards;
  @media (max-width: ${bp.sm}) { flex-direction: column; align-items: stretch; }
`;

const ViewToggle = styled.div`
  display: flex; background: white; border-radius: 12px;
  padding: 4px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.05); flex-shrink: 0;
  @media (max-width: ${bp.sm}) { width: 100%; }
`;

const ToggleBtn = styled.button`
  flex: 1; padding: 9px 18px; border: none; border-radius: 9px;
  cursor: pointer; font-size: 0.83rem; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: all 0.22s ease; white-space: nowrap;
  background: ${p => p.active ? 'linear-gradient(135deg,#0284c7,#0369a1)' : 'transparent'};
  color: ${p => p.active ? 'white' : '#64748b'};
  ${p => p.active && 'box-shadow: 0 3px 10px rgba(2,132,199,0.22);'}
  touch-action: manipulation; -webkit-tap-highlight-color: transparent;
  &:hover:not([disabled]) { ${p => !p.active && 'background:#f0f9ff; color:#0284c7;'} }
  svg { width: 14px; height: 14px; }
`;

// ===================== FILTERS =====================
const FiltersRow = styled.div`
  background: white; border-radius: 14px; padding: 14px 16px;
  margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.04);
  display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end;
  animation: ${fadeIn} 0.4s ease 0.15s backwards;
  @media (max-width: ${bp.sm}) { padding: 12px 14px; gap: 10px; }
`;

const FilterCol = styled.div`
  display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 140px;
  label { font-size: 0.65rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; }
`;

const StyledSelect = styled.select`
  width: 100%; padding: 9px 32px 9px 12px;
  border: 2px solid #e2e8f0; border-radius: 10px;
  font-size: 0.86rem; color: #334155; background: #f8fafc;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 11px center;
  cursor: pointer; transition: all 0.18s ease; font-weight: 500;
  touch-action: manipulation;
  &:hover { border-color: #bae6fd; }
  &:focus { outline: none; border-color: #0284c7; box-shadow: 0 0 0 3px rgba(2,132,199,0.1); background-color: white; }
  @media (max-width: ${bp.sm}) { font-size: 16px; height: 44px; }
`;

const StyledInput = styled.input`
  width: 100%; padding: 9px 12px;
  border: 2px solid #e2e8f0; border-radius: 10px;
  font-size: 0.86rem; color: #334155; background: #f8fafc;
  transition: all 0.18s ease; touch-action: manipulation;
  &:hover { border-color: #bae6fd; }
  &:focus { outline: none; border-color: #0284c7; box-shadow: 0 0 0 3px rgba(2,132,199,0.1); background: white; }
  @media (max-width: ${bp.sm}) { font-size: 16px; height: 44px; }
`;

// ===================== TABS =====================
const TabRow = styled.div`
  display: flex; gap: 3px; margin-bottom: 16px;
  background: #f1f5f9; border-radius: 11px; padding: 3px;
`;

const TabBtn = styled.button`
  flex: 1; padding: 9px 12px; border: none; border-radius: 9px;
  cursor: pointer; font-size: 0.82rem; font-weight: 600;
  transition: all 0.22s ease;
  background: ${p => p.active ? 'white' : 'transparent'};
  color: ${p => p.active ? '#0c4a6e' : '#64748b'};
  ${p => p.active && 'box-shadow: 0 1px 4px rgba(0,0,0,0.06);'}
  touch-action: manipulation; -webkit-tap-highlight-color: transparent; white-space: nowrap;
  &:hover:not([disabled]) { ${p => !p.active && 'color:#0284c7;'} }
`;

// ===================== TABLE CARD =====================
const TableCard = styled.div`
  background: white; border-radius: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05);
  overflow: hidden; border: 1px solid rgba(0,0,0,0.04);
  animation: ${popIn} 0.4s cubic-bezier(0.22,1,0.36,1);
  @media (max-width: ${bp.sm}) { border-radius: 14px; }
`;

// Desktop table — hidden on mobile
const TableScroll = styled.div`
  overflow-x: auto;
  @media (max-width: ${bp.md}) { display: none; }
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: #f1f5f9; }
  &::-webkit-scrollbar-thumb { background: #bae6fd; border-radius: 2px; }
`;

const DataTable = styled.table`
  width: 100%; border-collapse: separate; border-spacing: 0; min-width: 1180px;

  th {
    padding: 13px 16px; text-align: left; font-weight: 700; color: #64748b;
    font-size: 0.67rem; text-transform: uppercase; letter-spacing: 0.9px;
    background: linear-gradient(135deg,#f0f9ff,#f8fafc);
    border-bottom: 2px solid #e0f2fe; white-space: nowrap; position: sticky; top: 0; z-index: 4;
  }

  td {
    padding: 13px 16px; border-bottom: 1px solid #f1f5f9;
    font-size: 0.86rem; color: #334155; vertical-align: middle;
  }

  tbody tr {
    transition: background 0.15s ease;
    animation: ${staggerIn} 0.3s ease backwards;
    &:hover { background: linear-gradient(90deg,#f0f9ff,#f8fafc); }
    &:last-child td { border-bottom: none; }
    ${() => {
      let s = '';
      for (let i = 0; i < 20; i++) s += `&:nth-of-type(${i+1}){animation-delay:${i*0.025}s;}`;
      return s;
    }}
  }
`;

// ===================== MOBILE CARD LIST =====================
// Shown only on mobile — hidden on desktop
const MobileList = styled.div`
  display: none;
  @media (max-width: ${bp.md}) { display: flex; flex-direction: column; }
`;

const MobileResultCard = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  display: flex; align-items: flex-start; gap: 12px;
  animation: ${staggerIn} 0.3s ease backwards;
  animation-delay: ${p => p.$delay || '0s'};
  transition: background 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:last-child { border-bottom: none; }
  &:active { background: #f0f9ff; }
`;

const MobileCardBody = styled.div`
  flex: 1; min-width: 0;
`;

const MobileCardName = styled.div`
  font-size: 0.9rem; font-weight: 650; color: #1e293b;
  margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const MobileCardMeta = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 6px;
`;

const MobileCardScores = styled.div`
  display: flex; flex-wrap: wrap; gap: 3px; margin-top: 4px;
`;

const MobileCardRight = styled.div`
  display: flex; flex-direction: column; align-items: flex-end;
  gap: 6px; flex-shrink: 0;
`;

const MobileCardDate = styled.div`
  font-size: 0.7rem; color: #94a3b8; font-weight: 500; white-space: nowrap;
`;

// ===================== CLIENT CELL =====================
const ClientCell = styled.div`
  display: flex; align-items: center; gap: 10px;
`;

const Avatar = styled.div`
  width: 36px; height: 36px; border-radius: 10px;
  background: linear-gradient(135deg,${p=>p.c1||'#0284c7'},${p=>p.c2||'#38bdf8'});
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; color: white; font-size: 0.72rem; flex-shrink: 0;
  box-shadow: 0 2px 6px ${p=>p.c1||'#0284c7'}35;
  transition: transform 0.2s ease;
  tr:hover & { transform: scale(1.06); }
`;

const MobileAvatar = styled(Avatar)`
  width: 42px; height: 42px; border-radius: 12px; font-size: 0.78rem;
`;

const ClientMeta = styled.div`
  .name { font-weight: 600; color: #1e293b; font-size: 0.86rem; line-height: 1.3; }
  .sub  { font-size: 0.74rem; color: #94a3b8; margin-top: 1px; }
`;

// ===================== BADGES =====================
const ScorePill = styled.span`
  display: inline-flex; align-items: center;
  padding: 3px 8px; border-radius: 5px; font-size: 0.68rem; font-weight: 700;
  background: ${p=>p.bg}; color: ${p=>p.color}; border: 1px solid ${p=>p.border||'transparent'};
`;


const RiskBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 20px; font-size: 0.68rem; font-weight: 800;
  white-space: nowrap; text-transform: capitalize;
  background: ${p=>p.level==='critical'?'#fee2e2':p.level==='high'?'#fff7ed':p.level==='moderate'?'#fefce8':'#f1f5f9'};
  color: ${p=>p.level==='critical'?'#991b1b':p.level==='high'?'#c2410c':p.level==='moderate'?'#a16207':'#64748b'};
  border: 1px solid ${p=>p.level==='critical'?'#fecaca':p.level==='high'?'#fed7aa':p.level==='moderate'?'#fef08a':'#e2e8f0'};
`;

const DemographicsList = styled.div`
  display: grid; gap: 4px; min-width: 260px;
  .demo-row { display: grid; grid-template-columns: 78px minmax(0, 1fr); gap: 8px; align-items: start; }
  .demo-label { color: #94a3b8; font-size: 0.66rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.4px; }
  .demo-value { color: #334155; font-size: 0.76rem; font-weight: 600; overflow-wrap: anywhere; }
`;

const MobileDemographicsList = styled.div`
  display: flex; flex-wrap: wrap; gap: 5px; margin: 6px 0;
`;

const MobileDemoChip = styled.span`
  padding: 3px 7px; border-radius: 6px;
  background: #f8fafc; border: 1px solid #e2e8f0;
  color: #64748b; font-size: 0.68rem; font-weight: 650;
`;

const StatusDot = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 20px; font-size: 0.68rem; font-weight: 700;
  white-space: nowrap;
  background: ${p=>p.paid?'#f0fdf4':'#fffbeb'};
  color: ${p=>p.paid?'#16a34a':'#d97706'};
  border: 1px solid ${p=>p.paid?'#bbf7d0':'#fde68a'};
  &::before {
    content:''; width:5px; height:5px; border-radius:50%; flex-shrink:0;
    background:${p=>p.paid?'#16a34a':'#d97706'};
  }
`;

const TestTag = styled.span`
  padding: 3px 9px; border-radius: 6px; font-size: 0.75rem; font-weight: 600;
  background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd;
`;

const DateLabel = styled.span`
  font-size: 0.8rem; color: #64748b; font-weight: 500;
`;

// ── THE FIX: ViewBtn styled exactly like original ──────────────────────────
const ViewBtn = styled.button`
  padding: 7px 16px;
  background: linear-gradient(135deg, #0284c7, #0369a1);
  color: white; border: none; border-radius: 8px;
  font-size: 0.8rem; font-weight: 650; cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 2px 6px rgba(2,132,199,0.2);
  white-space: nowrap;
  touch-action: manipulation; -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(2,132,199,0.3);
  }
  &:active { transform: translateY(0); }
`;

// Mobile "View Details" button shown inside each mobile card
const MobileViewBtn = styled(ViewBtn)`
  padding: 6px 12px;
  font-size: 0.75rem;
  margin-top: 4px;
`;

// ===================== PAGINATION =====================
const PaginationRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 10px;
  @media (max-width: ${bp.sm}) { flex-direction: column; align-items: center; padding: 12px; gap: 8px; }
`;

const PageInfo = styled.span`
  font-size: 0.8rem; color: #94a3b8; font-weight: 500;
`;

const PageBtns = styled.div`
  display: flex; gap: 4px; flex-wrap: wrap; justify-content: center;
`;

const PageBtn = styled.button`
  padding: 7px 13px;
  border: 2px solid ${p=>p.active?'#0284c7':'#e2e8f0'};
  background: ${p=>p.active?'linear-gradient(135deg,#0284c7,#0369a1)':'white'};
  color: ${p=>p.active?'white':'#64748b'};
  border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 0.8rem;
  transition: all 0.18s ease; touch-action: manipulation;
  min-width: 36px; min-height: 36px;
  &:hover:not(:disabled) { border-color: #0284c7; color: ${p=>p.active?'white':'#0284c7'}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ===================== EMPTY / SKELETON =====================
const EmptyBlock = styled.div`
  text-align: center; padding: 56px 24px;
  animation: ${fadeIn} 0.4s ease;
  .icon {
    width: 64px; height: 64px; border-radius: 18px;
    background: linear-gradient(135deg,#e0f2fe,#bae6fd);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 18px; font-size: 1.6rem;
    animation: ${float} 4s ease-in-out infinite;
  }
  h3 { font-size: 1.05rem; font-weight: 700; color: #1e293b; margin: 0 0 5px; }
  p  { color: #94a3b8; font-size: 0.86rem; margin: 0 auto; max-width: 360px; line-height: 1.55; }
  @media (max-width: ${bp.sm}) { padding: 40px 16px; }
`;

const Bone = styled.div`
  height: ${p=>p.h||'13px'}; width: ${p=>p.w||'80px'}; border-radius: ${p=>p.r||'6px'};
  background: linear-gradient(90deg,#e0f2fe 25%,#bae6fd 50%,#e0f2fe 75%);
  background-size: 1600px 100%; animation: ${shimmer} 1.8s infinite linear;
  display: inline-block;
`;

// ===================== MODAL / BOTTOM SHEET =====================
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(12,74,110,0.4); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px; z-index: 1000;
  animation: ${fadeOverlay} 0.22s ease;

  @media (max-width: ${bp.md}) {
    align-items: center;
    padding: 10px;
  }
`;

const ModalContainer = styled.div`
  background: white; border-radius: 22px;
  max-width: 740px; width: 100%;
  max-height: 90vh; overflow-y: auto;
  padding: 32px 32px 28px;
  box-shadow: 0 25px 60px rgba(3,105,161,0.2);
  animation: ${slideUp} 0.38s cubic-bezier(0.22,1,0.36,1);

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #bae6fd; border-radius: 2px; }

  @media (max-width: ${bp.md}) {
    border-radius: 16px;
    width: min(100%, 420px);
    max-height: calc(100dvh - 20px);
    padding: 0;
    animation: ${slideUp} 0.32s cubic-bezier(0.22,1,0.36,1);
    display: flex;
    flex-direction: column;
  }
`;

const SheetHandle = styled.div`
  display: none;
  @media (max-width: ${bp.md}) {
    display: none;
  }
`;

const ModalScrollBody = styled.div`
  @media (max-width: ${bp.md}) {
    padding: 14px 16px 22px;
    overflow-y: auto; flex: 1;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
  }

  @media (max-width: ${bp.sm}) {
    padding: 12px 12px 18px;
  }
`;

const ModalHead = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 22px; gap: 10px;

  @media (max-width: ${bp.md}) {
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
`;

const ModalTitle = styled.h2`
  font-size: clamp(0.95rem, 3vw, 1.2rem); font-weight: 750;
  color: #0c4a6e; margin: 0; line-height: 1.3; flex: 1;
`;

const ModalActions = styled.div`
  display: flex; gap: 7px; align-items: center; flex-shrink: 0; flex-wrap: wrap;
  @media (max-width: ${bp.sm}) { width: 100%; }
`;

const DownloadBtn = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg,#0284c7,#0369a1);
  color: white; border: none; border-radius: 10px;
  font-weight: 650; font-size: 0.8rem; cursor: pointer;
  display: flex; align-items: center; gap: 5px;
  transition: all 0.22s ease;
  box-shadow: 0 2px 8px rgba(2,132,199,0.22);
  touch-action: manipulation; -webkit-tap-highlight-color: transparent;
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(2,132,199,0.32); }
  &:active { transform: scale(0.98); }
  @media (max-width: ${bp.sm}) { flex: 1; justify-content: center; padding: 7px 10px; font-size: 0.72rem; }
`;

const DownloadAllBtn = styled(DownloadBtn)`
  background: linear-gradient(135deg,#059669,#047857);
  box-shadow: 0 2px 8px rgba(5,150,105,0.22);
  &:hover { box-shadow: 0 4px 14px rgba(5,150,105,0.32); }
`;

const PreviewBtn = styled(DownloadBtn)`
  background: linear-gradient(135deg,#7c3aed,#5b21b6);
  box-shadow: 0 2px 8px rgba(124,58,237,0.22);
  &:hover { box-shadow: 0 4px 14px rgba(124,58,237,0.32); }
`;

const CloseBtn = styled.button`
  width: 34px; height: 34px; border-radius: 10px;
  border: 2px solid #e0f2fe; background: white;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #94a3b8; transition: all 0.18s ease; flex-shrink: 0;
  touch-action: manipulation;
  &:hover { border-color: #bae6fd; color: #0284c7; background: #f0f9ff; }
  svg { width: 15px; height: 15px; }
`;

// ===================== MULTI-TEST TABS =====================
const ResultsTabs = styled.div`
  display: flex; gap: 7px; margin-bottom: 18px;
  overflow-x: auto; padding-bottom: 8px;
  animation: ${fadeIn} 0.3s ease;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { height: 3px; }
  &::-webkit-scrollbar-thumb { background: #bae6fd; border-radius: 2px; }
`;

const ResultTab = styled.button`
  padding: 7px 13px;
  border: 2px solid ${p=>p.active?'#0284c7':'#e0f2fe'};
  background: ${p=>p.active?'linear-gradient(135deg,#0284c7,#0369a1)':'white'};
  color: ${p=>p.active?'white':'#0369a1'};
  border-radius: 8px; font-weight: 600; cursor: pointer;
  transition: all 0.18s ease; white-space: nowrap; font-size: 0.78rem; flex-shrink: 0;
  touch-action: manipulation;
  &:hover { border-color: #0284c7; }
`;

const MultiTestHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #e0f2fe;
`;

const ResultCounter = styled.div`
  font-size: 0.83rem; color: #0369a1; font-weight: 600;
`;

const PdfPreviewPanel = styled.div`
  border: 1px solid #bae6fd;
  border-radius: 14px;
  overflow: hidden;
  background: #f8fafc;
  box-shadow: 0 8px 24px rgba(15,23,42,0.08);
  margin-bottom: 18px;
  animation: ${fadeIn} 0.25s ease;
`;

const PdfPreviewBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 10px 12px; background: linear-gradient(135deg,#eff6ff,#e0f2fe);
  border-bottom: 1px solid #bae6fd; color: #0c4a6e; font-size: 0.82rem; font-weight: 700;
  span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const PdfCloseBtn = styled.button`
  border: 1px solid #bae6fd; background: white; color: #0369a1;
  border-radius: 8px; padding: 6px 10px; font-size: 0.74rem; font-weight: 700; cursor: pointer; flex-shrink: 0;
  &:hover { background: #f0f9ff; }
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: min(72vh, 780px);
  border: 0;
  background: white;
  display: block;
  @media (max-width: ${bp.sm}) { height: 68vh; }
`;

// ===================== MODAL CONTENT SECTIONS =====================
const InfoGrid = styled.div`
  background: linear-gradient(135deg,#f0f9ff,#e0f2fe);
  border-radius: 14px; padding: 16px 18px; margin-bottom: 20px;
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px; border: 1px solid #bae6fd;
  animation: ${fadeIn} 0.3s ease 0.1s backwards;
  @media (max-width: ${bp.sm}) { grid-template-columns: 1fr 1fr; padding: 10px; gap: 8px; margin-bottom: 12px; }
`;

const InfoItem = styled.div`
  min-width: 0;
  &.wide { grid-column: span 2; }
  .label { font-size: 0.64rem; font-weight: 700; color: #0369a1; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 3px; }
  .value { font-weight: 600; color: #0c4a6e; font-size: 0.88rem; line-height: 1.35; min-width: 0; overflow-wrap: anywhere; word-break: break-word; }
  @media (max-width: ${bp.sm}) {
    &.wide { grid-column: 1 / -1; }
    .label { font-size: 0.56rem; }
    .value { font-size: 0.72rem; }
  }
`;

const ScoresRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px; margin-bottom: 20px;
  @media (max-width: ${bp.sm}) { grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 12px; }
`;

const ScoreBox = styled.div`
  background: white; border-radius: 14px; padding: 18px 14px; text-align: center;
  border: 1px solid rgba(0,0,0,0.04); box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  position: relative; overflow: hidden;
  animation: ${popIn} 0.4s ease backwards;
  animation-delay: ${p=>p.idx?`${p.idx*0.07}s`:'0s'};
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
  &::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:${p=>p.accent||'#0284c7'}; }
  .title    { font-size:0.66rem; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.7px; margin-bottom:8px; }
  .score    { font-size:clamp(1.8rem,4vw,2.3rem); font-weight:800; color:${p=>p.accent||'#0284c7'}; line-height:1; margin-bottom:7px; }
  .severity { font-size:0.72rem; font-weight:700; color:${p=>p.accent||'#0284c7'}; background:${p=>p.accent||'#0284c7'}15; padding:3px 10px; border-radius:100px; display:inline-block; }
`;

const ClusterTitle = styled.h3`
  margin: 0 0 14px; font-size: 0.95rem; font-weight: 700;
  color: #0c4a6e; padding-bottom: 10px; border-bottom: 2px solid #e0f2fe;
`;

const ClustersGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px; margin-bottom: 20px;
  @media (max-width: ${bp.sm}) { grid-template-columns: repeat(2, 1fr); gap: 7px; margin-bottom: 12px; }
`;

const clusterColor = sev => ({Normal:'#10b981',Mild:'#f59e0b',Moderate:'#f97316',Severe:'#ef4444'}[sev]||'#94a3b8');

const ClusterCard = styled.div`
  background: white; border-radius: 12px; padding: 14px;
  border: 2px solid ${p=>clusterColor(p.severity)};
  animation: ${popIn} 0.4s ease backwards;
  .cluster-name     { font-size:0.7rem; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:7px; }
  .cluster-score    { font-size:1.6rem; font-weight:800; color:${p=>clusterColor(p.severity)}; margin-bottom:7px; }
  .cluster-severity { font-size:0.7rem; font-weight:700; color:white; background:${p=>clusterColor(p.severity)}; padding:3px 10px; border-radius:100px; display:inline-block; }
`;

const DiagnosticBox = styled.div`
  background: ${p=>p.priority===1?'#fee2e2':p.priority<=3?'#fef3c7':'#f0f9ff'};
  border-left: 4px solid ${p=>p.priority===1?'#dc2626':p.priority<=3?'#f59e0b':'#0284c7'};
  border-radius: 8px; padding: 14px; margin-bottom: 10px;
  animation: ${fadeIn} 0.3s ease backwards;
  .priority  { font-size:0.62rem; font-weight:800; text-transform:uppercase; letter-spacing:0.6px; color:${p=>p.priority===1?'#7f1d1d':p.priority<=3?'#78350f':'#0c4a6e'}; margin-bottom:4px; }
  .condition { font-size:0.9rem; font-weight:700; color:${p=>p.priority===1?'#991b1b':p.priority<=3?'#92400e':'#0c4a6e'}; margin-bottom:7px; }
  .output    { font-size:0.83rem; color:${p=>p.priority===1?'#7f1d1d':p.priority<=3?'#78350f':'#475569'}; line-height:1.5; margin-bottom:8px; }
  .steps     { font-size:0.78rem; color:${p=>p.priority===1?'#7f1d1d':p.priority<=3?'#78350f':'#64748b'}; ul{margin:6px 0 0 18px;padding:0;li{margin-bottom:3px;}} }
`;

const ResponsesSection = styled.div`
  margin-top: 22px;
  h3 { font-size:0.92rem; font-weight:700; color:#0c4a6e; margin-bottom:12px; display:flex; align-items:center; gap:7px; }
`;

const ResponseRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 11px 12px; border-radius: 9px; margin-bottom: 3px;
  transition: background 0.15s ease; gap: 14px;
  animation: ${staggerIn} 0.28s ease backwards;
  animation-delay: ${p=>p.idx?`${p.idx*0.018}s`:'0s'};
  &:hover { background: #f0f9ff; }
  &:nth-of-type(odd) { background: #f8fafc; }
  .q-num  { font-size:0.74rem; font-weight:700; color:#0284c7; min-width:24px; flex-shrink:0; }
  .q-text { flex:1; font-size:0.86rem; color:#334155; line-height:1.5; }
  .q-val  { font-weight:700; color:#0284c7; font-size:0.86rem; min-width:28px; text-align:center; background:rgba(2,132,199,0.08); padding:2px 8px; border-radius:5px; flex-shrink:0; }
  @media (max-width: ${bp.sm}) { padding:8px; gap: 8px; .q-num{font-size:0.66rem; min-width:20px;} .q-text{font-size:0.74rem;} .q-val{font-size:0.72rem; padding:2px 6px;} }
`;

const DescBlock = styled.div`
  margin-top: 16px; padding: 14px 16px;
  background: ${p=>p.$purple?'#f5f3ff':'#f0f9ff'};
  border-radius: 10px;
  border-left: 4px solid ${p=>p.$purple?'#7c3aed':'#0284c7'};
  h4 { margin:0 0 7px; color:${p=>p.$purple?'#5b21b6':'#0c4a6e'}; font-size:0.9rem; font-weight:700; }
  p  { margin:0; color:#475569; font-size:0.87rem; line-height:1.55; }
`;

const InlineAlert = styled.div`
  margin-bottom: 8px; padding: 12px 14px;
  background: ${p=>p.type==='CRITICAL'?'#fee2e2':'#fef3c7'};
  border-radius: 6px; border-left: 4px solid ${p=>p.type==='CRITICAL'?'#dc2626':'#f59e0b'};
  font-size: 0.88rem; line-height: 1.5;
  color: ${p=>p.type==='CRITICAL'?'#7f1d1d':'#78350f'};
`;

// ===================== POPULATION VIEW =====================
const PopCard = styled.div`
  background: white; border-radius: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05);
  overflow: hidden; border: 1px solid rgba(0,0,0,0.04);
  animation: ${popIn} 0.4s ease;
  @media (max-width: ${bp.sm}) { border-radius: 14px; }
`;

const PopToolbar = styled.div`
  padding: 16px 18px;
  background: linear-gradient(135deg,#f0f9ff,#e0f2fe);
  border-bottom: 1px solid #bae6fd;
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 12px;
  @media (max-width: ${bp.sm}) { flex-direction: column; align-items: stretch; }
`;

const RespondentLabel = styled.div`
  display: flex; align-items: center; gap: 7px;
  font-size: 0.85rem; color: #475569;
  strong { font-size:1rem; background:linear-gradient(135deg,#0284c7,#0369a1); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
`;

const ExportBtns = styled.div`
  display: flex; gap: 6px; flex-wrap: wrap;
  @media (max-width: ${bp.sm}) { width: 100%; }
`;

const ExportBtn = styled.button`
  padding: 7px 14px; color: white; border: none; border-radius: 8px;
  cursor: pointer; font-size: 0.76rem; font-weight: 700;
  transition: all 0.22s ease; display: flex; align-items: center; gap: 4px;
  touch-action: manipulation;
  background: ${p=>p.v==='csv'?'linear-gradient(135deg,#0284c7,#0369a1)':p.v==='stata'?'linear-gradient(135deg,#2563eb,#1d4ed8)':'linear-gradient(135deg,#ea580c,#c2410c)'};
  box-shadow: ${p=>p.v==='csv'?'0 2px 8px rgba(2,132,199,0.22)':p.v==='stata'?'0 2px 8px rgba(37,99,235,0.22)':'0 2px 8px rgba(234,88,12,0.22)'};
  &:hover { transform: translateY(-1px); }
  &:active { transform: scale(0.98); }
  @media (max-width: ${bp.sm}) { flex: 1; justify-content: center; min-height: 40px; }
`;

const PopTableScroll = styled.div`
  overflow-x: auto; overflow-y: auto; max-height: 600px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { width:4px; height:4px; }
  &::-webkit-scrollbar-thumb { background:#bae6fd; border-radius:2px; }
`;

const PopTable = styled.table`
  width: 100%; border-collapse: separate; border-spacing: 0;
  th {
    padding:11px 13px; text-align:left; font-weight:700; color:#64748b;
    font-size:0.65rem; text-transform:uppercase; letter-spacing:0.7px;
    background:linear-gradient(135deg,#f0f9ff,#f8fafc);
    border-bottom:2px solid #e0f2fe; white-space:nowrap; position:sticky; top:0; z-index:4;
  }
  td { padding:11px 13px; border-bottom:1px solid #f1f5f9; font-size:0.83rem; color:#334155; }
  tbody tr {
    transition:background 0.12s ease;
    animation:${staggerIn} 0.22s ease backwards;
    &:hover { background:#f0f9ff; }
    &:last-child td { border-bottom:none; }
    ${()=>{let s='';for(let i=0;i<30;i++)s+=`&:nth-of-type(${i+1}){animation-delay:${i*0.015}s;}`;return s;}}
  }
`;

const StickyTh = styled.th`
  position:sticky !important; left:0; z-index:8 !important;
  background:linear-gradient(135deg,#f0f9ff,#f8fafc) !important; min-width:140px;
`;
const StickyTd = styled.td`
  position:sticky; left:0; z-index:3; background:white; font-weight:600;
  tr:hover & { background:#f0f9ff; }
`;
const QHeader = styled.th`
  min-width:200px; text-align:center !important;
  font-size:0.64rem !important; font-weight:600 !important;
  padding:9px 5px !important; line-height:1.3;
`;
const CodeCell = styled.code`
  display:inline-block; padding:2px 7px; background:#e0f2fe; border-radius:4px;
  font-size:0.76rem; color:#0369a1; font-weight:600;
  font-family:'SF Mono','Fira Code',monospace;
`;
const PopFooter = styled.div`
  padding:12px 18px; background:linear-gradient(135deg,#f0f9ff,#f8fafc);
  border-top:1px solid #e0f2fe; font-size:0.76rem; color:#94a3b8;
  display:flex; align-items:center; gap:5px;
  strong { color:#0369a1; }
  svg { width:13px; height:13px; flex-shrink:0; color:#0284c7; }
`;

// ===================== POPULATION ANALYSIS COMPONENTS =====================
const AnalysisBlock = styled.div`
  margin-bottom: 26px; animation:${fadeIn} 0.4s ease backwards;
  h4 { font-size:0.95rem; font-weight:700; color:#0c4a6e; margin:0 0 14px; padding-bottom:9px; border-bottom:2px solid #e0f2fe; }
`;

const StatsGrid = styled.div`
  display:grid; grid-template-columns:repeat(auto-fit,minmax(130px,1fr)); gap:10px; margin-bottom:14px;
  @media (max-width:${bp.sm}) { grid-template-columns:repeat(2,1fr); }
`;

const MiniStat = styled.div`
  background:linear-gradient(135deg,#f0f9ff,#f8fafc); border-radius:12px; padding:14px;
  border-left:3px solid ${p=>p.accent||'#0284c7'};
  transition:transform 0.2s ease;
  animation:${popIn} 0.32s ease backwards; animation-delay:${p=>p.idx?`${p.idx*0.05}s`:'0s'};
  &:hover { transform:translateY(-2px); }
  .label { font-size:0.64rem; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.6px; margin-bottom:5px; }
  .value { font-size:1.5rem; font-weight:800; color:#0c4a6e; line-height:1; }
  .sub   { font-size:0.72rem; color:#94a3b8; margin-top:3px; }
`;

const ChartBox = styled.div`
  background:linear-gradient(135deg,#f0f9ff,#f8fafc); border-radius:14px;
  padding:18px; border:1px solid #e0f2fe; margin-bottom:14px;
  animation:${fadeIn} 0.4s ease 0.1s backwards;
`;

const BarChartWrap = styled.div`
  .title { font-size:0.86rem; font-weight:700; color:#0c4a6e; margin-bottom:14px; }
  .bars  { display:flex; align-items:flex-end; justify-content:space-around; height:160px; gap:5px; margin-bottom:10px; }
  .bar-wrap { flex:1; display:flex; flex-direction:column; align-items:center; height:100%; justify-content:flex-end; }
  .bar   { width:100%; max-width:46px; background:linear-gradient(180deg,#38bdf8,#0284c7); border-radius:5px 5px 0 0; position:relative; transition:all 0.3s ease; cursor:pointer;
    &:hover { opacity:0.82; .tip{opacity:1;transform:translateX(-50%) translateY(-3px);} }
  }
  .tip   { position:absolute; bottom:100%; left:50%; transform:translateX(-50%); background:#0c4a6e; color:white; padding:4px 8px; border-radius:6px; font-size:0.68rem; font-weight:600; white-space:nowrap; margin-bottom:5px; opacity:0; transition:all 0.18s ease; pointer-events:none;
    &::after { content:''; position:absolute; top:100%; left:50%; transform:translateX(-50%); border:3px solid transparent; border-top-color:#0c4a6e; }
  }
  .labels { display:flex; justify-content:space-around; gap:5px; }
  .lbl   { flex:1; text-align:center; font-size:0.66rem; color:#94a3b8; font-weight:600; }
`;

const PercentileList = styled.div`
  margin-top:12px;
  h5 { font-size:0.8rem; font-weight:700; color:#0369a1; margin:0 0 9px; }
`;

const PctRow = styled.div`
  display:flex; justify-content:space-between; align-items:center;
  padding:9px 12px; border-radius:7px; margin-bottom:2px;
  transition:background 0.15s ease;
  &:hover { background:#e0f2fe; }
  &:nth-of-type(odd) { background:#f0f9ff; }
  .pct-label { font-weight:600; color:#64748b; font-size:0.82rem; }
  .pct-value { font-size:0.95rem; font-weight:700; color:#0284c7; }
`;

// ===================== SVG ICONS =====================
const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);
const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
  </svg>
);

const ResultsIllustration = () => (
  <svg viewBox="0 0 180 150" fill="none">
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0;0,-3;0,0" dur="4s" repeatCount="indefinite"/>
      <rect x="20" y="85" width="18" height="40" rx="4" fill="white" fillOpacity="0.2" stroke="white" strokeOpacity="0.3" strokeWidth="1">
        <animate attributeName="height" values="40;50;40" dur="3s" repeatCount="indefinite"/>
        <animate attributeName="y" values="85;75;85" dur="3s" repeatCount="indefinite"/>
      </rect>
      <rect x="44" y="60" width="18" height="65" rx="4" fill="white" fillOpacity="0.25" stroke="white" strokeOpacity="0.3" strokeWidth="1">
        <animate attributeName="height" values="65;55;65" dur="3.5s" repeatCount="indefinite"/>
        <animate attributeName="y" values="60;70;60" dur="3.5s" repeatCount="indefinite"/>
      </rect>
      <rect x="68" y="45" width="18" height="80" rx="4" fill="#67e8f9" fillOpacity="0.3" stroke="#67e8f9" strokeOpacity="0.5" strokeWidth="1">
        <animate attributeName="height" values="80;70;80" dur="2.8s" repeatCount="indefinite"/>
        <animate attributeName="y" values="45;55;45" dur="2.8s" repeatCount="indefinite"/>
      </rect>
      <rect x="92" y="70" width="18" height="55" rx="4" fill="white" fillOpacity="0.2" stroke="white" strokeOpacity="0.3" strokeWidth="1">
        <animate attributeName="height" values="55;65;55" dur="3.2s" repeatCount="indefinite"/>
        <animate attributeName="y" values="70;60;70" dur="3.2s" repeatCount="indefinite"/>
      </rect>
    </g>
    <line x1="15" y1="128" x2="115" y2="128" stroke="white" strokeOpacity="0.3" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M25 100 C45 85,55 75,77 55 C90 45,95 80,105 75" stroke="#67e8f9" strokeOpacity="0.6" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="4 3">
      <animate attributeName="stroke-dashoffset" values="0;-7" dur="1s" repeatCount="indefinite"/>
    </path>
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0;0,3;0,0" dur="5s" repeatCount="indefinite"/>
      <rect x="120" y="30" width="50" height="55" rx="8" fill="white" fillOpacity="0.12" stroke="white" strokeOpacity="0.25" strokeWidth="1.5"/>
      <text x="145" y="55" textAnchor="middle" fill="white" fillOpacity="0.8" fontSize="14" fontWeight="bold">92</text>
      <text x="145" y="70" textAnchor="middle" fill="#67e8f9" fillOpacity="0.6" fontSize="7" fontWeight="600">SCORE</text>
    </g>
    <circle cx="130" cy="105" r="4" fill="#67e8f9" fillOpacity="0.4">
      <animate attributeName="r" values="4;6;4" dur="2.5s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

const HeroBackgroundSVG = () => (
  <svg viewBox="0 0 800 200" preserveAspectRatio="none">
    <defs>
      <pattern id="heroGridR2" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeOpacity="0.04" strokeWidth="1"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#heroGridR2)"/>
    <circle cx="650" cy="40" r="60" fill="white" fillOpacity="0.03"/>
    <circle cx="100" cy="160" r="80" fill="white" fillOpacity="0.02"/>
  </svg>
);

// ===================== HELPERS =====================
const avatarColors = [
  ['#0284c7','#38bdf8'],['#0369a1','#7dd3fc'],['#0c4a6e','#0ea5e9'],
  ['#0891b2','#22d3ee'],['#0d9488','#2dd4bf'],['#059669','#34d399'],
  ['#6366f1','#818cf8'],['#8b5cf6','#a78bfa'],
];
const getColors   = name => avatarColors[(name||'').charCodeAt(0) % avatarColors.length];
const getInitials = (f,l) => `${(f||'')[0]||''}${(l||'')[0]||''}`.toUpperCase();
const getSeverityStyle = sev => ({
  Normal:           {color:'#16a34a', bg:'#f0fdf4', border:'#bbf7d0'},
  Mild:             {color:'#65a30d', bg:'#f7fee7', border:'#d9f99d'},
  Moderate:         {color:'#ca8a04', bg:'#fefce8', border:'#fef08a'},
  Severe:           {color:'#ea580c', bg:'#fff7ed', border:'#fed7aa'},
  'Extremely Severe':{color:'#dc2626', bg:'#fef2f2', border:'#fecaca'},
}[sev]||{color:'#64748b', bg:'#f1f5f9', border:'#e2e8f0'});
const getSeverityColor = s => getSeverityStyle(s).color;
const formatDate = d => d
  ? new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'2-digit'})
  : '–';

// ===================== COMPONENT =====================
const Results = () => {
  const { searchQuery } = useSearch();

  const [results,              setResults]              = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [selectedResult,       setSelectedResult]       = useState(null);
  const [page,                 setPage]                 = useState(1);
  const [totalPages,           setTotalPages]           = useState(1);
  const [testFilter,           setTestFilter]           = useState('');
  const [tests,                setTests]                = useState([]);
  const [filteredResults,      setFilteredResults]      = useState([]);
  const [resultFilter,         setResultFilter]         = useState('all');
  const [orgNameQuery,         setOrgNameQuery]         = useState('');
  const [populationAnalysis,   setPopulationAnalysis]   = useState(null);
  const [resultTab,            setResultTab]            = useState('individual');
  const [assignmentId,         setAssignmentId]         = useState(null);
  const [viewMode,             setViewMode]             = useState('individual');
  const [populationTestFilter, setPopulationTestFilter] = useState('');
  const [populationResults,    setPopulationResults]    = useState([]);
  const [populationLoading,    setPopulationLoading]    = useState(false);
  const [multiTestResults,     setMultiTestResults]     = useState(null);
  const [selectedTestIdx,      setSelectedTestIdx]      = useState(0);
  const [downloadingAll,       setDownloadingAll]       = useState(false);
  const [reportPreviewUrl,    setReportPreviewUrl]    = useState('');
  const [reportPreviewName,   setReportPreviewName]   = useState('');
  const [reportPreviewLoading,setReportPreviewLoading]= useState(false);

  useEffect(() => { loadTests(); },   []);
  useEffect(() => { loadResults(); }, [page, testFilter]);

  useEffect(() => {
    if (!searchQuery) { setFilteredResults(results); return; }
    const q = searchQuery.toLowerCase();
    setFilteredResults(results.filter(r => {
      const name  = `${r.Client?.firstName||''} ${r.Client?.lastName||''}`.toLowerCase();
      const tName = r.TestCatalog?.shortName?.toLowerCase() || '';
      const date  = r.completedAt ? new Date(r.completedAt).toLocaleDateString().toLowerCase() : '';
      return name.includes(q) || tName.includes(q) || date.includes(q);
    }));
  }, [searchQuery, results]);

  useEffect(() => () => {
    if (reportPreviewUrl) {
      window.URL.revokeObjectURL(reportPreviewUrl);
    }
  }, [reportPreviewUrl]);

  useEffect(() => {
    document.body.classList.toggle('result-detail-open', Boolean(selectedResult));

    if (!selectedResult) return () => document.body.classList.remove('result-detail-open');

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('result-detail-open');
    };
  }, [selectedResult]);

  const loadTests = async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/tests`);
      setTests(res.data);
    } catch (e) { console.error(e); }
  };

  const loadResults = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (testFilter) params.testId = testFilter;
      const res = await axios.get(`${API_URL}/practitioner/results`, { params });
      setResults(res.data.results);
      setFilteredResults(res.data.results);
      setTotalPages(res.data.totalPages);
    } catch (e) { console.error(e);
    } finally { setLoading(false); }
  };

  // ── THE KEY FIX: viewResult matches original signature exactly ────────────
  const viewResult = async (resultId, assignId = null) => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/results/${resultId}`);
      closeReportPreview();
      setSelectedResult(res.data);
      setAssignmentId(assignId);
      setResultTab('individual');
      setPopulationAnalysis(null);
      setMultiTestResults(null);
      setSelectedTestIdx(0);

      if (assignId) {
        try {
          const multiRes = await axios.get(`${API_URL}/tests/results/${assignId}`);
          if (multiRes.data.results && multiRes.data.results.length > 1) {
            setMultiTestResults(multiRes.data.results);
            setSelectedResult(multiRes.data.results[0]);
            setSelectedTestIdx(0);
          }
        } catch (e) { console.log('Multi-test not available:', e); }

        try {
          const a = await axios.get(`${API_URL}/practitioner/results/analysis/${assignId}`);
          setPopulationAnalysis(a.data);
        } catch (e) { console.log('Population analysis not available:', e); }
      }
    } catch (e) { console.error('Failed to load result:', e); }
  };

  function closeReportPreview() {
    setReportPreviewUrl((currentUrl) => {
      if (currentUrl) window.URL.revokeObjectURL(currentUrl);
      return '';
    });
    setReportPreviewName('');
  }

  function closeResultModal() {
    closeReportPreview();
    setSelectedResult(null);
  }

  const viewReport = async () => {
    try {
      if (!selectedResult?.id) { alert('Unable to generate report preview'); return; }
      setReportPreviewLoading(true);
      const res = await axios.get(`${API_URL}/payments/view-report/${selectedResult.id}`, {
        responseType: 'blob',
      });
      if (res.data.type?.includes('application/json')) {
        const reader = new FileReader();
        reader.onload = () => { const err = JSON.parse(reader.result); alert(`Error: ${err.error}`); };
        reader.readAsText(res.data); return;
      }

      closeReportPreview();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = `${selectedResult.TestCatalog?.shortName || 'Assessment'}_Report_${selectedResult.Client?.lastName || 'Client'}_${new Date().toISOString().split('T')[0]}.pdf`;
      setReportPreviewName(filename);
      setReportPreviewUrl(url);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to preview report.');
    } finally {
      setReportPreviewLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      if (!selectedResult?.id) { alert('Unable to generate report'); return; }
      const res = await axios.get(`${API_URL}/payments/download-report/${selectedResult.id}`, {
        responseType: 'blob',
      });
      if (res.data.type?.includes('application/json')) {
        const reader = new FileReader();
        reader.onload = () => { const err = JSON.parse(reader.result); alert(`Error: ${err.error}`); };
        reader.readAsText(res.data); return;
      }
      const url  = window.URL.createObjectURL(new Blob([res.data], {type:'application/pdf'}));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `${selectedResult.TestCatalog?.shortName}_Report_${selectedResult.Client?.lastName}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link); link.click(); link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to download report.');
    }
  };

  const downloadAllReports = async () => {
    if (!multiTestResults?.length) { alert('No results to download'); return; }
    try {
      setDownloadingAll(true);
      for (let i = 0; i < multiTestResults.length; i++) {
        const result = multiTestResults[i];
        try {
          const res = await axios.get(`${API_URL}/payments/download-report/${result.id}`, {responseType:'blob'});
          if (res.data.type?.includes('application/json')) continue;
          const url  = window.URL.createObjectURL(new Blob([res.data],{type:'application/pdf'}));
          const link = document.createElement('a');
          link.href  = url;
          link.setAttribute('download', `${result.TestCatalog?.shortName}_Report_${multiTestResults[0].Client?.lastName}_${new Date().toISOString().split('T')[0]}.pdf`);
          document.body.appendChild(link); link.click(); link.parentElement.removeChild(link);
          window.URL.revokeObjectURL(url);
          if (i < multiTestResults.length - 1) await new Promise(r => setTimeout(r, 500));
        } catch {}
      }
    } finally { setDownloadingAll(false); }
  };

  const loadPopulationResults = async (testId) => {
    if (!testId) { setPopulationResults([]); return; }
    try {
      setPopulationLoading(true);
      const res = await axios.get(`${API_URL}/practitioner/results`, { params: { testId, limit: 1000 } });
      setPopulationResults(res.data.results);
    } catch { setPopulationResults([]);
    } finally { setPopulationLoading(false); }
  };

  const NULL_DISPLAY = 'Null';

  const formatDemographicValue = (value) => {
    if (value === null || value === undefined || value === '') return NULL_DISPLAY;
    const text = String(value).trim();
    return text || NULL_DISPLAY;
  };

  const getClientGenderDisplay = (client) => {
    if (!client) return NULL_DISPLAY;
    const gender = String(client.gender || '').trim();
    const custom = String(client.genderCustom || '').trim();
    if (gender.toLowerCase() === 'other' && custom) return custom;
    return custom || gender || NULL_DISPLAY;
  };

  const getRiskLabel = (risk) => risk?.level || 'low';

  const getClientDemographics = (client) => [
    ['Age', formatDemographicValue(client?.age)],
    ['Gender', getClientGenderDisplay(client)],
    ['Email', formatDemographicValue(client?.email)],
    ['Phone', formatDemographicValue(client?.phone)],
    ['Occupation', formatDemographicValue(client?.occupation)],
    ['Education', formatDemographicValue(client?.education)],
    ['Marital', formatDemographicValue(client?.maritalStatus)]
  ];

  const renderDemographicsList = (client) => (
    <DemographicsList>
      {getClientDemographics(client).map(([label, value]) => (
        <div className="demo-row" key={label}>
          <span className="demo-label">{label}</span>
          <span className="demo-value">{value}</span>
        </div>
      ))}
    </DemographicsList>
  );

  const renderMobileDemographics = (client) => (
    <MobileDemographicsList>
      {getClientDemographics(client).map(([label, value]) => (
        <MobileDemoChip key={label}>{label}: {value}</MobileDemoChip>
      ))}
    </MobileDemographicsList>
  );

  const getOtherText = (valueObject) => {
    if (!valueObject || typeof valueObject !== 'object') return '';
    const candidates = [
      valueObject.other,
      valueObject.otherValue,
      valueObject.otherText,
      valueObject.customValue,
      valueObject.specify,
      valueObject.text,
      valueObject.label,
    ];
    const resolved = candidates.find((v) => typeof v === 'string' && v.trim());
    return resolved ? resolved.trim() : '';
  };

  const getResponseValue = (responses, questionId) => {
    if (!responses) return '';
    if (Array.isArray(responses)) {
      const f = responses.find(r => r.questionId === questionId);
      return f ? fmtVal(f.value) : '';
    }
    const v = responses[questionId];
    if (v && typeof v === 'object' && 'value' in v) return fmtVal(v.value);
    return fmtVal(v ?? '');
  };
  const fmtVal = v => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') {
      const optionValue = 'value' in v ? v.value : v;
      const optionText = String(optionValue ?? '').trim();
      if (optionText.toLowerCase() === 'other') {
        return getOtherText(v) || optionText;
      }
      return optionText || getOtherText(v);
    }
    return String(v);
  };

  const exportRawData = (format) => {
    if (!populationResults.length) { alert('No data to export'); return; }
    const selectedTest = tests.find(t => t.id === populationTestFilter);
    if (!selectedTest) return;
    const questions = selectedTest.questions || [];
    const headers = ['ResponseID','ClientName','ClientEmail','Age','Gender','DateCompleted',...questions.map(q=>q.text)];
    const rows = populationResults.map(r => [
      r.id,
      `${r.Client?.firstName||''} ${r.Client?.lastName||''}`,
      r.Client?.email||'', r.Client?.age||'', getClientGenderDisplay(r.Client)||'',
      new Date(r.completedAt).toISOString().split('T')[0],
      ...questions.map(q => getResponseValue(r.responses, q.id))
    ]);
    const dl = (content, type, ext) => {
      const blob = new Blob([content], {type});
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.setAttribute('download', `${selectedTest.shortName}_RawData_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(a); a.click(); a.parentElement.removeChild(a);
    };
    if (format === 'csv') {
      const csv = [headers.map(h=>`"${h.replace(/"/g,'""')}"`).join(','), ...rows.map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(','))].join('\n');
      dl(csv, 'text/csv;charset=utf-8;', 'csv');
    } else {
      const tsv = [headers.join('\t'), ...rows.map(r=>r.map(c=>String(c).replace(/\t/g,' ')).join('\t'))].join('\n');
      dl(tsv, 'text/plain;charset=utf-8;', format==='stata'?'tsv':'txt');
    }
  };

  const visibleResults = filteredResults.filter(r => {
    const isOrg  = r.TestAssignment?.linkType === 'organization';
    const scope  = resultFilter==='all' ? true : resultFilter==='org' ? isOrg : !isOrg;
    const nameOk = !orgNameQuery.trim() ||
      (r.TestAssignment?.organizationName||'').toLowerCase().includes(orgNameQuery.toLowerCase().trim());
    return scope && nameOk;
  });

  const totalResults = results.length;
  const paidCount    = results.filter(r => r.isPaid).length;

  const renderSkeleton = () => (
    <>
      {Array.from({length:5}).map((_,i) => (
        <div key={i} style={{display:'flex', gap:12, padding:'14px 16px', borderBottom:'1px solid #f1f5f9'}}>
          <Bone w="42px" h="42px" r="12px"/>
          <div style={{flex:1}}>
            <Bone w="55%" h="14px" style={{marginBottom:8}}/>
            <Bone w="40%" h="12px" style={{marginBottom:6}}/>
            <div style={{display:'flex', gap:6}}><Bone w="55px" h="20px" r="20px"/><Bone w="40px" h="20px" r="20px"/></div>
          </div>
        </div>
      ))}
    </>
  );

  // Shared individual result content (used in both modal body)
  const renderIndividualContent = () => (
    <>
      <InfoGrid>
        <InfoItem><div className="label">Name</div><div className="value">{selectedResult.Client?.firstName} {selectedResult.Client?.lastName}</div></InfoItem>
        <InfoItem><div className="label">Age</div><div className="value">{formatDemographicValue(selectedResult.Client?.age)}</div></InfoItem>
        <InfoItem><div className="label">Gender</div><div className="value">{getClientGenderDisplay(selectedResult.Client)}</div></InfoItem>
        <InfoItem className="wide"><div className="label">Email</div><div className="value" title={formatDemographicValue(selectedResult.Client?.email)}>{formatDemographicValue(selectedResult.Client?.email)}</div></InfoItem>
        <InfoItem><div className="label">Phone</div><div className="value" title={formatDemographicValue(selectedResult.Client?.phone)}>{formatDemographicValue(selectedResult.Client?.phone)}</div></InfoItem>
        <InfoItem><div className="label">Occupation</div><div className="value">{formatDemographicValue(selectedResult.Client?.occupation)}</div></InfoItem>
        <InfoItem><div className="label">Education</div><div className="value">{formatDemographicValue(selectedResult.Client?.education)}</div></InfoItem>
        <InfoItem><div className="label">Marital Status</div><div className="value">{formatDemographicValue(selectedResult.Client?.maritalStatus)}</div></InfoItem>
        <InfoItem><div className="label">Date</div><div className="value">{formatDate(selectedResult.completedAt)}</div></InfoItem>
        <InfoItem><div className="label">Follow-up</div><div className="value"><RiskBadge level={getRiskLabel(selectedResult.followUpRisk)}>{getRiskLabel(selectedResult.followUpRisk)}</RiskBadge></div></InfoItem>
      </InfoGrid>

      {selectedResult.followUpRisk?.isFlagged && (
        <div style={{marginBottom:16}}>
          <h4 style={{margin:'0 0 10px', color:'#1e293b', fontSize:'0.92rem', fontWeight:700}}>🚩 Follow-up Flag</h4>
          <InlineAlert type={selectedResult.followUpRisk.level === 'critical' ? 'CRITICAL' : 'WARNING'}>
            <strong>{selectedResult.followUpRisk.recommendedAction}.</strong>
            {selectedResult.followUpRisk.reasons?.length > 0 && (
              <ul style={{margin:'8px 0 0 18px', padding:0}}>
                {selectedResult.followUpRisk.reasons.map((reason, idx) => <li key={idx}>{reason}</li>)}
              </ul>
            )}
          </InlineAlert>
        </div>
      )}

      <ScoresRow>
        {selectedResult.interpretation && Object.entries(selectedResult.interpretation)
          .filter(([k,v]) => k && !k.startsWith('_') && v!=null && typeof v==='object' && 'severity' in v && 'score' in v)
          .map(([k,v],idx) => (
            <ScoreBox key={k} accent={getSeverityColor(v?.severity)} idx={idx}>
              <div className="title">{k}</div>
              <div className="score">{v?.score}</div>
              <div className="severity">{v?.severity}</div>
            </ScoreBox>
          ))}
      </ScoresRow>

      {selectedResult.interpretation?._description && typeof selectedResult.interpretation._description === 'string' && (
        <DescBlock>
          <h4>Test Description</h4>
          <p>{selectedResult.interpretation._description}</p>
        </DescBlock>
      )}

      {selectedResult.interpretation?._recommendations && typeof selectedResult.interpretation._recommendations === 'string' && (
        <DescBlock $purple>
          <h4>Recommendations</h4>
          <p>{selectedResult.interpretation._recommendations}</p>
        </DescBlock>
      )}

      {selectedResult.alerts?.length > 0 && (
        <div style={{marginTop:16}}>
          <h4 style={{margin:'0 0 10px', color:'#1e293b', fontSize:'0.92rem', fontWeight:700}}>⚠️ Clinical Alerts</h4>
          {selectedResult.alerts.map((alert, idx) => (
            <InlineAlert key={idx} type={alert.type}>{alert.message}</InlineAlert>
          ))}
        </div>
      )}

      {selectedResult.scores?._mseClusterAnalysis && (
        <div style={{marginTop:22}}>
          <ClusterTitle>🧠 MSE Diagnostic Clusters & Clinical Decision Pathways</ClusterTitle>
          <ClusterTitle style={{fontSize:'0.88rem', borderBottom:'1px solid #e0f2fe', marginBottom:'10px', paddingBottom:'7px'}}>Cluster Scores</ClusterTitle>
          <ClustersGrid>
            {selectedResult.scores._mseClusterAnalysis.clusters && Object.entries(selectedResult.scores._mseClusterAnalysis.clusters).map(([k,c]) => (
              <ClusterCard key={k} severity={c.severity}>
                <div className="cluster-name">
                  {k==='risk'?'Risk Cluster':k==='neurocognitive'?'Neurocognitive':k==='psychosis'?'Psychosis':k==='mood'?'Mood':k==='activation'?'Activation':k}
                </div>
                <div className="cluster-score">{c.standardized.toFixed(2)}</div>
                <div className="cluster-severity">{c.severity}</div>
              </ClusterCard>
            ))}
          </ClustersGrid>
          <ClusterTitle style={{fontSize:'0.88rem', borderBottom:'1px solid #e0f2fe', marginBottom:'10px', paddingBottom:'7px'}}>Diagnostic Recommendations</ClusterTitle>
          {selectedResult.scores._mseClusterAnalysis.diagnosticPathways?.map((pw, i) => (
            <DiagnosticBox key={i} priority={pw.priority}>
              <div className="priority">Priority {pw.priority}: {pw.severity}</div>
              <div className="condition">{pw.condition}</div>
              <div className="output">{pw.output}</div>
              {pw.nextSteps?.length > 0 && (
                <div className="steps">
                  <strong>Next Steps:</strong>
                  <ul>{pw.nextSteps.map((s,i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
            </DiagnosticBox>
          ))}
        </div>
      )}

      <ResponsesSection>
        <h3>📝 Individual Responses</h3>
        {selectedResult.TestCatalog?.questions?.map((q, idx) => (
          <ResponseRow key={q.id} idx={idx}>
            <span className="q-num">{idx+1}.</span>
            <span className="q-text">{q.text}</span>
            <span className="q-val">{getResponseValue(selectedResult.responses, q.id)||'–'}</span>
          </ResponseRow>
        ))}
      </ResponsesSection>
    </>
  );

  return (
    <PageWrapper>
      <Container>
        {/* ── Hero ─────────────────────────────────── */}
        <HeroSection>
          <HeroBg><HeroBackgroundSVG/></HeroBg>
          <HeroGrid>
            <HeroLeft>
              <HeroBadge><BadgeDot/><BadgeText>Assessment Analytics</BadgeText></HeroBadge>
              <HeroTitle>Client Results</HeroTitle>
              <HeroDesc>View and analyze assessment outcomes, download reports, and explore population-level data for research.</HeroDesc>
              <HeroStats>
                <HeroStat><div className="number">{totalResults}</div><div className="label">Total Results</div></HeroStat>
                <HeroStatDiv/>
                <HeroStat><div className="number">{paidCount}</div><div className="label">Reports Paid</div></HeroStat>
                <HeroStatDiv/>
                <HeroStat><div className="number">{tests.length}</div><div className="label">Active Tests</div></HeroStat>
              </HeroStats>
            </HeroLeft>
            <HeroVisual><ResultsIllustration/></HeroVisual>
          </HeroGrid>
        </HeroSection>

        {/* ── View Toggle ───────────────────────────── */}
        <ToolbarRow>
          <ViewToggle>
            <ToggleBtn active={viewMode==='individual'} onClick={()=>setViewMode('individual')}>
              <ListIcon/> Individual Results
            </ToggleBtn>
            <ToggleBtn active={viewMode==='population'} onClick={()=>setViewMode('population')}>
              <GridIcon/> Population Data
            </ToggleBtn>
          </ViewToggle>
        </ToolbarRow>

        {viewMode==='individual' ? (
          <>
            <FiltersRow>
              <FilterCol>
                <label>Test</label>
                <StyledSelect value={testFilter} onChange={e=>{setTestFilter(e.target.value);setPage(1);}}>
                  <option value="">All Tests</option>
                  {tests.map(t=><option key={t.id} value={t.id}>{t.shortName}</option>)}
                </StyledSelect>
              </FilterCol>
              <FilterCol>
                <label>Org name search</label>
                <StyledInput
                  value={orgNameQuery}
                  placeholder="Enter organization name"
                  onChange={e=>setOrgNameQuery(e.target.value)}
                />
              </FilterCol>
            </FiltersRow>

            <TabRow style={{marginBottom:14}}>
              <TabBtn active={resultFilter==='all'}    onClick={()=>setResultFilter('all')}>All</TabBtn>
              <TabBtn active={resultFilter==='direct'} onClick={()=>setResultFilter('direct')}>Direct</TabBtn>
              <TabBtn active={resultFilter==='org'}    onClick={()=>setResultFilter('org')}>Org</TabBtn>
            </TabRow>

            <TableCard>
              {loading ? renderSkeleton() : visibleResults.length===0 ? (
                <EmptyBlock>
                  <div className="icon">{searchQuery?'🔍':'📋'}</div>
                  <h3>{searchQuery?'No Results Found':'No Results Yet'}</h3>
                  <p>{searchQuery?`No results match "${searchQuery}"`:'Results from your clients will appear here.'}</p>
                </EmptyBlock>
              ) : (
                <>
                  {/* ── Desktop Table ──────────────────── */}
                  <TableScroll>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Demographics</th>
                          <th>Test</th>
                          <th>Scores</th>
                          <th>Follow-up</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleResults.map(result => {
                          const [c1,c2] = getColors(result.Client?.firstName);
                          return (
                            <tr key={result.id}>
                              <td>
                                <ClientCell>
                                  <Avatar c1={c1} c2={c2}>
                                    {getInitials(result.Client?.firstName, result.Client?.lastName)}
                                  </Avatar>
                                  <ClientMeta>
                                    <div className="name">{result.Client?.firstName} {result.Client?.lastName}</div>
                                    <div className="sub">{formatDemographicValue(result.Client?.email)}</div>
                                  </ClientMeta>
                                </ClientCell>
                              </td>
                              <td>{renderDemographicsList(result.Client)}</td>
                              <td><TestTag>{result.TestCatalog?.shortName}</TestTag></td>
                              <td>
                                <div style={{display:'flex', flexWrap:'wrap', gap:3}}>
                                  {result.interpretation && Object.entries(result.interpretation)
                                    .filter(([k,v]) => k && !k.startsWith('_') && v!=null && typeof v==='object' && 'severity' in v && 'score' in v)
                                    .map(([k,v]) => {
                                      const s = getSeverityStyle(v?.severity);
                                      return <ScorePill key={k} bg={s.bg} color={s.color} border={s.border}>{k.charAt(0).toUpperCase()}: {v?.score}</ScorePill>;
                                    })}
                                </div>
                              </td>
                              <td>
                                <RiskBadge level={getRiskLabel(result.followUpRisk)}>
                                  {getRiskLabel(result.followUpRisk)}
                                </RiskBadge>
                              </td>
                              <td><DateLabel>{formatDate(result.completedAt)}</DateLabel></td>
                              <td><StatusDot paid={result.isPaid}>{result.isPaid?'Paid':'Pending'}</StatusDot></td>
                              <td>
                                {/* ── FIXED: exact same call as original ── */}
                                <ViewBtn
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewResult(result.id, result.TestAssignmentId);
                                  }}
                                >
                                  View Details
                                </ViewBtn>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </DataTable>
                  </TableScroll>

                  {/* ── Mobile Card List ───────────────── */}
                  <MobileList>
                    {visibleResults.map((result, i) => {
                      const [c1,c2] = getColors(result.Client?.firstName);
                      const scoreEntries = result.interpretation
                        ? Object.entries(result.interpretation).filter(([k,v]) => k && !k.startsWith('_') && v!=null && typeof v==='object' && 'severity' in v && 'score' in v)
                        : [];
                      return (
                        <MobileResultCard key={result.id} $delay={`${i*0.04}s`}>
                          <MobileAvatar c1={c1} c2={c2}>
                            {getInitials(result.Client?.firstName, result.Client?.lastName)}
                          </MobileAvatar>

                          <MobileCardBody>
                            <MobileCardName>
                              {result.Client?.firstName} {result.Client?.lastName}
                            </MobileCardName>
                            <MobileCardMeta>
                              <TestTag>{result.TestCatalog?.shortName}</TestTag>
                              <RiskBadge level={getRiskLabel(result.followUpRisk)}>
                                {getRiskLabel(result.followUpRisk)}
                              </RiskBadge>
                            </MobileCardMeta>
                            {renderMobileDemographics(result.Client)}
                            {scoreEntries.length > 0 && (
                              <MobileCardScores>
                                {scoreEntries.slice(0,3).map(([k,v]) => {
                                  const s = getSeverityStyle(v?.severity);
                                  return <ScorePill key={k} bg={s.bg} color={s.color} border={s.border}>{k.charAt(0).toUpperCase()}: {v?.score}</ScorePill>;
                                })}
                                {scoreEntries.length > 3 && (
                                  <ScorePill bg="#f1f5f9" color="#64748b">+{scoreEntries.length-3}</ScorePill>
                                )}
                              </MobileCardScores>
                            )}
                            {/* ── FIXED: explicit button, same call as original ── */}
                            <MobileViewBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                viewResult(result.id, result.TestAssignmentId);
                              }}
                            >
                              View Details
                            </MobileViewBtn>
                          </MobileCardBody>

                          <MobileCardRight>
                            <MobileCardDate>{formatDate(result.completedAt)}</MobileCardDate>
                            <StatusDot paid={result.isPaid}>{result.isPaid?'Paid':'Pending'}</StatusDot>
                          </MobileCardRight>
                        </MobileResultCard>
                      );
                    })}
                  </MobileList>

                  {totalPages > 1 && (
                    <PaginationRow>
                      <PageInfo>Page {page} of {totalPages}</PageInfo>
                      <PageBtns>
                        <PageBtn disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</PageBtn>
                        {Array.from({length:Math.min(totalPages,5)},(_,i)=>{
                          let num;
                          if (totalPages<=5) num=i+1;
                          else if (page<=3) num=i+1;
                          else if (page>=totalPages-2) num=totalPages-4+i;
                          else num=page-2+i;
                          return <PageBtn key={num} active={page===num} onClick={()=>setPage(num)}>{num}</PageBtn>;
                        })}
                        <PageBtn disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}>Next →</PageBtn>
                      </PageBtns>
                    </PaginationRow>
                  )}
                </>
              )}
            </TableCard>
          </>
        ) : (
          /* ── Population View ──────────────────────── */
          <>
            <FiltersRow>
              <FilterCol>
                <label>Select Assessment</label>
                <StyledSelect
                  value={populationTestFilter}
                  onChange={e=>{setPopulationTestFilter(e.target.value); loadPopulationResults(e.target.value);}}
                  style={{minWidth:240}}
                >
                  <option value="">Choose a test to view data…</option>
                  {tests.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </StyledSelect>
              </FilterCol>
            </FiltersRow>

            {populationTestFilter ? (
              <PopCard>
                {populationLoading ? (
                  <EmptyBlock>
                    <div className="icon">📊</div>
                    <h3>Loading Data…</h3><p>Fetching population results for analysis.</p>
                  </EmptyBlock>
                ) : populationResults.length===0 ? (
                  <EmptyBlock>
                    <div className="icon">📭</div>
                    <h3>No Results Found</h3><p>No client results available for this test yet.</p>
                  </EmptyBlock>
                ) : (
                  <>
                    <PopToolbar>
                      <RespondentLabel>
                        <strong>{populationResults.length}</strong>
                        <span>respondents • Export for statistical analysis</span>
                      </RespondentLabel>
                      <ExportBtns>
                        <ExportBtn v="csv"   onClick={()=>exportRawData('csv')}>📊 CSV</ExportBtn>
                        <ExportBtn v="stata" onClick={()=>exportRawData('stata')}>📈 STATA</ExportBtn>
                        <ExportBtn v="spss"  onClick={()=>exportRawData('spss')}>🔍 SPSS</ExportBtn>
                      </ExportBtns>
                    </PopToolbar>
                    <PopTableScroll>
                      <PopTable>
                        <thead>
                          <tr>
                            <StickyTh>Client</StickyTh>
                            <th style={{minWidth:60}}>Age</th>
                            <th style={{minWidth:75}}>Gender</th>
                            <th style={{minWidth:105}}>Completed</th>
                            {(tests.find(t=>t.id===populationTestFilter)?.questions||populationResults[0]?.TestCatalog?.questions)?.map(q=>(
                              <QHeader key={q.id}>{q.text}</QHeader>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {populationResults.map(r=>(
                            <tr key={r.id}>
                              <StickyTd>{r.Client?.firstName} {r.Client?.lastName}</StickyTd>
                              <td>{r.Client?.age||'–'}</td>
                              <td>{getClientGenderDisplay(r.Client) || '–'}</td>
                              <td><DateLabel>{formatDate(r.completedAt)}</DateLabel></td>
                              {(tests.find(t=>t.id===populationTestFilter)?.questions||populationResults[0]?.TestCatalog?.questions)?.map(q=>(
                                <td key={q.id} style={{textAlign:'center'}}>
                                  <CodeCell>{getResponseValue(r.responses,q.id)||'–'}</CodeCell>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </PopTable>
                    </PopTableScroll>
                    <PopFooter>
                      <InfoIcon/>
                      <span><strong>Data Matrix:</strong> Each row = one respondent with raw responses per item.</span>
                    </PopFooter>
                  </>
                )}
              </PopCard>
            ) : (
              <TableCard>
                <EmptyBlock>
                  <div className="icon">📊</div>
                  <h3>Population Data View</h3>
                  <p>Select a test above to view raw assessment data in matrix format. Export all client responses to CSV or STATA for statistical analysis.</p>
                </EmptyBlock>
              </TableCard>
            )}
          </>
        )}

        {/* ── Detail Modal / Bottom Sheet ───────────── */}
        {selectedResult && (
          <Overlay onClick={closeResultModal}>
            <ModalContainer onClick={e=>e.stopPropagation()}>
              <SheetHandle/>
              <ModalScrollBody>
                <ModalHead>
                  <ModalTitle>
                    {selectedResult.TestCatalog?.name} — Results
                  </ModalTitle>
                  <ModalActions>
                    <PreviewBtn onClick={viewReport} disabled={reportPreviewLoading}>
                      {reportPreviewLoading ? '⏳ Loading...' : reportPreviewUrl ? '👁️ Refresh Preview' : '👁️ View Report'}
                    </PreviewBtn>
                    {multiTestResults?.length > 1 ? (
                      <DownloadAllBtn onClick={downloadAllReports} disabled={downloadingAll}>
                        {downloadingAll ? '⏳ Downloading All...' : '📥 Download All'}
                      </DownloadAllBtn>
                    ) : (
                      <DownloadBtn onClick={downloadReport}>📥 Download Report</DownloadBtn>
                    )}
                    <CloseBtn onClick={closeResultModal}><XIcon/></CloseBtn>
                  </ModalActions>
                </ModalHead>

                {reportPreviewUrl && (
                  <PdfPreviewPanel>
                    <PdfPreviewBar>
                      <span>PDF Preview: {reportPreviewName || 'Assessment report'}</span>
                      <PdfCloseBtn onClick={closeReportPreview}>Close Preview</PdfCloseBtn>
                    </PdfPreviewBar>
                    <PdfFrame title="PDF report preview" src={reportPreviewUrl} />
                  </PdfPreviewPanel>
                )}

                {/* Multi-test tabs */}
                {multiTestResults?.length > 1 && (
                  <>
                    <ResultsTabs>
                      {multiTestResults.map((result, idx) => (
                        <ResultTab
                          key={idx}
                          active={selectedTestIdx===idx}
                          onClick={()=>{closeReportPreview(); setSelectedTestIdx(idx); setSelectedResult(result);}}
                        >
                          {idx+1}. {result.TestCatalog?.shortName}
                        </ResultTab>
                      ))}
                    </ResultsTabs>
                    <MultiTestHeader>
                      <ResultCounter>Result {selectedTestIdx+1} of {multiTestResults.length}</ResultCounter>
                    </MultiTestHeader>
                  </>
                )}

                {/* Org population tab switcher */}
                {populationAnalysis?.linkType==='organization' && (
                  <TabRow>
                    <TabBtn active={resultTab==='individual'} onClick={()=>setResultTab('individual')}>Individual</TabBtn>
                    <TabBtn active={resultTab==='population'} onClick={()=>setResultTab('population')}>
                      Population ({populationAnalysis.totalRespondents})
                    </TabBtn>
                  </TabRow>
                )}

                {/* Individual tab */}
                {resultTab==='individual' && renderIndividualContent()}

                {/* Population tab */}
                {resultTab==='population' && populationAnalysis && (
                  <div>
                    <div style={{marginBottom:18}}>
                      <h3 style={{fontSize:'1rem', fontWeight:700, color:'#0c4a6e', margin:'0 0 3px'}}>
                        Organization: {populationAnalysis.organizationName}
                      </h3>
                      <p style={{color:'#94a3b8', fontSize:'0.85rem', margin:0}}>
                        Based on {populationAnalysis.totalRespondents} completed assessments
                      </p>
                    </div>

                    {Object.entries(populationAnalysis.analysis).map(([dimName,stats]) => (
                      <AnalysisBlock key={dimName}>
                        <h4>{dimName}</h4>
                        <StatsGrid>
                          <MiniStat accent="#0284c7" idx={0}><div className="label">Mean</div><div className="value">{stats.mean}</div><div className="sub">Avg response</div></MiniStat>
                          <MiniStat accent="#0ea5e9" idx={1}><div className="label">Median</div><div className="value">{stats.median}</div><div className="sub">Middle value</div></MiniStat>
                          <MiniStat accent="#0891b2" idx={2}><div className="label">Std Dev</div><div className="value">{stats.standardDeviation}</div><div className="sub">Score spread</div></MiniStat>
                          <MiniStat accent="#0369a1" idx={3}><div className="label">Range</div><div className="value">{stats.min}–{stats.max}</div><div className="sub">Min to Max</div></MiniStat>
                        </StatsGrid>

                        {stats.scoreDistribution && (
                          <ChartBox>
                            <BarChartWrap>
                              <div className="title">Score Distribution</div>
                              <div className="bars">
                                {stats.scoreDistribution.map((bin,idx) => {
                                  const maxC = Math.max(...stats.scoreDistribution.map(b=>b.count));
                                  const h = maxC > 0 ? (bin.count/maxC)*100 : 0;
                                  return (
                                    <div className="bar-wrap" key={idx}>
                                      <div className="bar" style={{height:`${Math.max(h,2)}%`}}>
                                        <div className="tip">{bin.count} ({bin.percentage}%)</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="labels">
                                {stats.scoreDistribution.map((bin,idx) => (
                                  <div className="lbl" key={idx}>{bin.min.toFixed(0)}–{bin.max.toFixed(0)}</div>
                                ))}
                              </div>
                            </BarChartWrap>
                          </ChartBox>
                        )}

                        {stats.percentiles && (
                          <PercentileList>
                            <h5>Percentiles</h5>
                            {[['10th',stats.percentiles.p10],['25th',stats.percentiles.p25],['50th (Median)',stats.percentiles.p50],['75th',stats.percentiles.p75],['90th',stats.percentiles.p90]].map(([lbl,val]) => (
                              <PctRow key={lbl}>
                                <span className="pct-label">{lbl} Percentile</span>
                                <span className="pct-value">{val}</span>
                              </PctRow>
                            ))}
                          </PercentileList>
                        )}
                      </AnalysisBlock>
                    ))}
                  </div>
                )}
              </ModalScrollBody>
            </ModalContainer>
          </Overlay>
        )}
      </Container>
    </PageWrapper>
  );
};

export default Results;
